import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box } from "@gemini/core";
import { useAdsContext } from "@/ads/context";
import { useEffect, useRef } from "react";
import { useI18n } from "@/shared/hooks";
import { AdFallbackIcon } from "./AdFallbackIcon";
import { useShowAds } from "../hooks/useShowAds";
/**
 * this component is used to display an ad slot
 *
 */
export const AdContainer = ({ adSlotId, width, height, display, showLabel = false, position, top, showFallback = false, justifyContent = "center", maxHeight = "initial", customFallback, dynamicSlot = false, }) => {
    const { displaySlot, isGptInitialized, loadedSlots, displayDynamicAdSlot, hasDeviceTypeChanged, userDevice, adSlots, } = useAdsContext();
    const { isAllSlotsLoaded } = useShowAds();
    const { t } = useI18n();
    const displayedSlots = useRef({});
    const isDisplayed = isAllSlotsLoaded([adSlotId]);
    const deviceTypeChanged = useRef(undefined); // to check if device type has changed only once
    /**
     * useEffect to display the ad slot on the page load
     * check if GDPR is Ready
     * check if the consent has changed
     * check if GPT is initialized
     * check if the slot has been loaded
     * check if the slot has been refreshed
     * refresh the slot
     * if the slot has been refreshed, set refreshed to true
     * if refresh if the consent has changed
     * or if adblock have been disabled
     * @param {boolean} isGDPRReady
     * @param {boolean} isGptInitialized
     * @param {string} adSlotId
     * @param {string} purposesConsent
     * @param {boolean} loadedSlots
     */
    useEffect(() => {
        if (!isGptInitialized)
            return;
        // display only if this specific slot hasn't been displayed yet
        if (!displayedSlots.current[adSlotId] && !hasDeviceTypeChanged) {
            dynamicSlot
                ? displayDynamicAdSlot(adSlots === null || adSlots === void 0 ? void 0 : adSlots.find((slot) => slot.id === adSlotId))
                : displaySlot(adSlotId);
            displayedSlots.current[adSlotId] = true;
        }
    }, [
        isGptInitialized,
        displaySlot,
        adSlotId,
        loadedSlots,
        displayDynamicAdSlot,
        dynamicSlot,
        hasDeviceTypeChanged,
        adSlots,
    ]);
    useEffect(() => {
        if (hasDeviceTypeChanged &&
            deviceTypeChanged.current !== userDevice &&
            dynamicSlot) {
            // check if the slot is not still destroyed
            if (Object.keys(loadedSlots).includes(adSlotId))
                return;
            googletag.cmd.push(() => {
                //redefine the slot
                displayDynamicAdSlot(adSlots === null || adSlots === void 0 ? void 0 : adSlots.find((slot) => slot.id === adSlotId));
            });
            deviceTypeChanged.current = userDevice;
        }
    }, [
        hasDeviceTypeChanged,
        userDevice,
        displayDynamicAdSlot,
        dynamicSlot,
        adSlotId,
        loadedSlots,
        adSlots,
    ]);
    return (_jsxs(_Fragment, { children: [!isDisplayed && customFallback ? customFallback : null, _jsxs(Box, { justifyContent: justifyContent, alignItems: "center", alignSelf: "center", display: display, flexDirection: "column", position: position, top: top, maxHeight: !isDisplayed && customFallback ? "0" : maxHeight, children: [showLabel && (_jsx(Box, { as: "span", textAlign: "center", typography: "typography.body.14.regular", height: "sizing.20", children: isDisplayed && t("advertising.slot.title") })), _jsx(Box, { as: "div", id: adSlotId, width: width, height: height, justifyContent: justifyContent, alignItems: "center", display: "flex", backgroundColor: showFallback && !isDisplayed ? "color.background.light" : null, borderWidth: showFallback && !isDisplayed ? "borderWidth.1" : null, borderStyle: showFallback && !isDisplayed ? "solid" : null, borderColor: showFallback && !isDisplayed ? "color.border.light.default" : null, children: showFallback && _jsx(AdFallbackIcon, {}) })] })] }));
};
